<template>
<b-container fluid>
  <h1 class="my-1">Part Info (Confidential)</h1>
  <b-alert variant="danger">{{alert}}</b-alert>
  <b-row align-h="center">
    <b-form inline @submit="search" class="mx-4 my-1">
      <b-form-input placeholder="Part Number" v-model="form.num" class="mx-1" />
      <b-form-input placeholder="Part Description" v-model="form.desc" class="mx-1" />
      <b-form-input placeholder="Part Location" v-model="form.location" class="mx-1" />
      <b-btn type="submit" variant="primary">Search</b-btn>
    </b-form>
  </b-row>
  <div v-if="this.items.length != 0" style="text-align: center;">
    <em><strong>These values are not affected by the filter!</strong></em> &nbsp; &nbsp;
    <strong>Total Qty: </strong> {{totalQty}} &nbsp;
    <strong>Total Cost: </strong> {{totalCost | formatDollars}}
  </div>
  <b-row>
    <b-col cols=9>
      <b-form-input v-model="filter" placeholder="Filter" class="m-1" />
    </b-col>
    <b-col>
      <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage"  class="my-1" />
    </b-col>
  </b-row>
  <b-table :fields="columns" :items="items" :busy=busy
    :per-page="perPage" :current-page="currentPage"
    :sort-by="'num'"
    :sort-compare="sortCompare" :filter="filter" @filtered="onFilter"
    class="max25-table"
  >
    <template slot="location" slot-scope="data">
      <div v-if="data.item.location">
        <div>{{data.item.locGroup}}>{{data.item.location}}</div>
        <div v-if="!!data.item.defLoc && data.item.location !== data.item.defLoc">
          <em>{{data.item.locGroup}}>{{data.item.defLoc}}</em>
        </div>
      </div>
      <div v-else>
        No Location Found... the item may be out of stock<br/>
        <span v-if="!!data.item.defLoc">Its default location is: {{ data.item.defLoc }}</span>
      </div>
    </template>
    <template slot="qty" slot-scope="data">
      {{Math.round(Number(data.item.qty) * 10) / 10}} {{data.item.uomCode}} &nbsp;
      <i class="fas fa-check" title="Pickable" v-if="data.item.pickable"></i>
      <i class="fas fa-times" title="Not Pickable" v-else></i>
      <br>
      <span class="small">{{Number(data.item.qtyCommitted)}} Committed</span>
    </template>
    <template slot="lastVendorAndDate" slot-scope="data">
      <div
        v-if="data.item.lastVendorAndDate"
        v-html="data.item.lastVendorAndDate.split('),').join('),<br/>')"
      ></div>
    </template>
    <template slot="partLastCost" slot-scope="data">
      <span v-if="data.item.partLastCost">{{data.item.partLastCost | formatDollars}}</span>
    </template>
    <template slot="vendorPartNumber" slot-scope="data">
      <span v-if="data.item.vendorName">{{data.item.vendorName}}</span><span v-else><em>No Default Vendor</em></span>
      <br v-if="data.item.vendorPartNumber && data.item.vendorName">
      <span v-if="data.item.vendorPartNumber">{{data.item.vendorPartNumber}}</span>
    </template>
    <template slot="partMinCost" slot-scope="data">
      {{data.item.partMinCost | formatDollars}}
    </template>
    <template slot="partMaxCost" slot-scope="data">
      {{data.item.partMaxCost | formatDollars}}
    </template>
    <template slot="partAvgCost" slot-scope="data">
      {{data.item.partAvgCost | formatDollars}}
    </template>
    <template slot="totalCost" slot-scope="data">
      {{data.item.partAvgCost * data.item.qty | formatDollars}}
    </template>
    <template slot="price" slot-scope="data">
      <span v-if="data.item.price == null" title="This part has no product associated with it.">NP</span>
      <span v-else>{{data.item.price | formatDollars}}</span>
    </template>
    <template slot="weight" slot-scope="data">
      {{Number(data.item.weight)}} {{data.item.weightuomcode}}
    </template>
  </b-table>
</b-container>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
const BASE_API = process.env.VUE_API_ROOT;

export default {
  mounted() {
    if (Object.keys(this.$route.query).length > 0) {
      Object.keys(this.$route.query).forEach((index, key) => {
        if (this.form[index] === '') {
          this.form[index] = this.$route.query[index];
        }
      });
      this.search();
    }
  },
  data() {
    return {
      alert: '',
      form: {
        num: '',
        desc: '',
        location: '',
      },
      busy: false,
      currentPage: 1,
      totalRows: 0,
      perPage: 20,
      filter: '',
      columns: [
        { key: 'num', label: 'Part #', sortable: true },
        { key: 'description', label: 'Description', sortable: true },
        { key: 'location', label: 'Location / Default (if different)', sortable: true },
        { key: 'qty', label: 'Qty / Pickable / Committed', sortable: true },
        { key: 'lastVendorAndDate', label: 'Last Vendors', sortable: true },
        { key: 'partLastCost', label: 'Last Cost', sortable: true },
        { key: 'vendorPartNumber', label: 'Default Vendor / Part #', sortable: true },
        { key: 'partMinCost', label: 'Min Cost / UOM', sortable: true },
        { key: 'partMaxCost', label: 'Max Cost / UOM', sortable: true },
        { key: 'partAvgCost', label: 'Avg. Cost Last Yr / UOM', sortable: true },
        { key: 'totalCost', label: 'Inventory Cost', sortable: true },
        { key: 'price', label: 'Price/UOM', sortable: true },
        { key: 'weight', label: 'Weight/UOM', sortable: true },
      ],
      items: [],
    };
  },
  computed: {
    totalQty() {
      const uoms = new Set(this.items.map( i => i['uomCode'] ))
      if(uoms.size > 1) {
        return 'Mixed UOMs'
      } else {
        return `${Math.round(this.items.map(x => Number(x.qty)).reduce((partial_sum, a) => partial_sum + a, 0) * 10) / 10} ${uoms.values().next().value}`;
      }
    },
    totalCost() {
      return this.items.map(x => Number(x['partAvgCost']) * Number(x['qty'])).reduce((partial_sum, a) => partial_sum + a, 0);
    },
  },
  methods: {
    search(e) {
      if (!!e) e.preventDefault();
      this.busy = true;
      axios({
        method: 'post',
        headers: { 'Content-Type': 'text/plain' },
        url: `${BASE_API}/fishbowl/parts/search-info`,
        data: this.form,
      }).then(response => {
        this.items = response.data;
        this.totalRows = this.items.length;
        this.currentPage = 1;
        this.busy = false;
      }).catch((error) => {
        this.alert = 'Search Failed. See console for more information';
        console.error(error);
      });
    },
    onFilter(items) {
      this.totalRows = items.length;
      this.currentPage = 1;
    },
    sortCompare(a, b, key) {
      if (key == 'totalCost') {
        const aCost = Number(a['partAvgCost']) * Number(a['qty']),
              bCost = Number(b['partAvgCost']) * Number(b['qty']);
              console.log(a, b, aCost, bCost, aCost < bCost ? -1 : aCost > bCost ? 1 : 0)
        return aCost < bCost ? -1 : aCost > bCost ? 1 : 0;
      } else if (key == 'weight') {
        return Number(a['weight']) < Number(b['weight']) ? -1 : Number(a['weight']) > Number(b['weight']) ? 1 : 0;
      } else return null;
    },
  }
}
</script>

<style>

.max25-table td, .max25-table th {
  max-width: 25vw;
}

</style>



// WEBPACK FOOTER //
// src/components/inventory/PartInfo.vue